/**
 * Project registry — the systems built and operated by the laboratory.
 *
 * Publication boundary: every entry describes purpose and observable
 * properties only. The mechanisms of research layers that are not published
 * yet are deliberately absent — described at the level of what they
 * guarantee, never how. No legal posture is claimed for that boundary; the
 * mechanisms are simply not out. Agent Native Universe (MIT) and Balansis
 * (AGPL-3.0, on PyPI) are already public, so both may be described in full.
 */

export interface Project {
  slug: string;
  name: string;
  tagline: string;
  status: 'production' | 'open-source' | 'research' | 'internal';
  statusLabel: string;
  domains: string[];
  summary: string;
  body: string[];
  links?: { label: string; href: string }[];
  /** Slugs of related research directions. */
  research: string[];
}

export const projects: Project[] = [
  {
    slug: 'studyninja',
    name: 'StudyNinja',
    tagline: 'Adaptive learning platform',
    status: 'production',
    statusLabel: 'In production',
    domains: ['AI', 'Education', 'Knowledge Graphs'],
    summary:
      'A learning platform for school students: a subject knowledge graph, gap diagnostics and next-step selection. This is where the laboratory’s research meets real users instead of demos.',
    body: [
      'StudyNinja is the proving ground of the laboratory. Every layer of our research — knowledge modelling, mastery estimation, adaptive sequencing — ships here first and is judged by one metric only: whether a struggling student recovers their trajectory.',
      'The platform maintains a knowledge graph per subject, diagnoses where exactly a student is stuck, and selects the next smallest step that produces a visible win. Motivation is born from success, not gifted — the product is engineered around that principle.',
      'StudyNinja runs in production with live users, which keeps the research honest: a model that only works on a benchmark does not survive here.',
    ],
    links: [{ label: 'studyninja.ru', href: 'https://studyninja.ru' }],
    research: ['adaptive-mastery', 'knowledge-graphs'],
  },
  {
    slug: 'knowledgebase-ai',
    name: 'KnowledgeBase AI',
    tagline: 'Knowledge platform',
    status: 'production',
    statusLabel: 'In production',
    domains: ['Knowledge Graphs', 'Semantic Retrieval', 'AI'],
    summary:
      'An engine that turns documents and a domain into a connected model: entities, relations, semantic search and provenance. It lives apart from any single product and is reused across projects.',
    body: [
      'KnowledgeBase AI is the laboratory’s knowledge layer as a standalone system. It ingests documents and domain structure and produces a connected model — entities, relations, semantic retrieval, and the provenance of every piece of knowledge.',
      'Keeping it separate from any single product is deliberate: the same engine powers education scenarios in StudyNinja and internal research tooling, which forces the abstractions to stay general.',
    ],
    research: ['knowledge-graphs', 'agent-orchestration'],
  },
  {
    slug: 'agent-native-universe',
    name: 'Agent Native Universe',
    tagline: 'Open-source agent runtime',
    status: 'open-source',
    statusLabel: 'Open source · MIT',
    domains: ['Multi-Agent Systems', 'Distributed Systems', 'Open Source'],
    summary:
      'A runtime for self-organizing agents: relationship negotiation, a resource economy, byzantine agreement between nodes and recursive organization into groups. MIT-licensed — download it and check.',
    body: [
      'Agent Native Universe (ANU) explores what software looks like when agents — not humans — are the first-class users. Instead of an orchestrator handing out tasks, ANU agents negotiate relationships with each other, pay for resources inside a built-in economy, and reach byzantine agreement without a central authority.',
      'Groups of agents can fold into a single composite agent and unfold back — organization becomes recursive, and structure emerges from interaction rather than configuration.',
      'ANU is the laboratory’s public research artifact: released under MIT, versioned, and open to inspection. It doubles as prior art for the agent-native line of research.',
    ],
    links: [
      { label: 'GitHub — StudyLabPro/agent-native-universe', href: 'https://github.com/StudyLabPro/agent-native-universe' },
    ],
    research: ['agent-native-software', 'multi-agent-consensus'],
  },
  {
    slug: 'magicbrain',
    name: 'MagicBrain',
    tagline: 'Neural research core',
    status: 'open-source',
    statusLabel: 'Open source · AGPL-3.0',
    domains: ['Spiking Networks', 'Local Learning', 'Neuromorphic AI'],
    summary:
      'A research core: spiking networks, local learning, grown rather than hand-designed structure. The source is out — architecture is encoded as a compact genome and grown by a developmental process, and you can read exactly how.',
    body: [
      'MagicBrain is the laboratory’s own line of research into brain-like computation: impulse-based networks, learning rules that stay local to the connection, and network structure that develops instead of being designed by hand.',
      'It is published rather than described: the library is on GitHub under AGPL-3.0 with a commercial alternative, and a companion playground runs it for real — training with held-out evaluation, neurogenesis stage by stage, damage and repair, and a comparison against the compensated arithmetic of Balansis. The numbers in that playground are generated from actual runs, including the ones that do not flatter the approach.',
    ],
    links: [
      { label: 'GitHub — StudyLabPro/MagicBrain', href: 'https://github.com/StudyLabPro/MagicBrain' },
      { label: 'GitHub — MagicBrain Playground', href: 'https://github.com/StudyLabPro/magicbrain-playground' },
    ],
    research: ['grown-networks', 'compensated-numerics'],
  },
  {
    slug: 'balansis',
    name: 'Balansis',
    tagline: 'Numerical foundation',
    status: 'open-source',
    statusLabel: 'Open source · AGPL-3.0',
    domains: ['Numerical Computing', 'Reliability', 'Open Source'],
    summary:
      'A computation library designed so that division by zero, overflow and precision loss do not take the pipeline down. A foundation under the other layers — published on GitHub and installable from PyPI.',
    body: [
      'Balansis asks a simple question with expensive consequences: why should an entire computation die because one value hit a singularity? The library is built so that division by zero, overflow and accumulated precision loss degrade gracefully instead of destroying the result.',
      'It sits underneath the other layers of the stack as their numerical foundation. Unlike the research layers, it is out: the source is on GitHub, releases are on PyPI, and the mechanism is in the code — read it rather than take the guarantees on trust.',
    ],
    links: [
      { label: 'GitHub — StudyLabPro/Balansis', href: 'https://github.com/StudyLabPro/Balansis' },
      { label: 'PyPI — balansis', href: 'https://pypi.org/project/balansis/' },
    ],
    research: ['compensated-numerics'],
  },
  {
    slug: 'uwt',
    name: 'UWT',
    tagline: 'Theoretical research track',
    status: 'research',
    statusLabel: 'Research',
    domains: ['Theory', 'Scientific Method'],
    summary:
      'A separate research line with an experiment registry, tests and a prepared preprint package. It sets the bar: a claim that cannot be checked does not ship.',
    body: [
      'UWT (Unified Wholeness Theory) is the laboratory’s theoretical track, run with the discipline of an experimental one: a registry of experiments, automated tests over the claims, and a preprint package prepared for publication.',
      'Its role inside the laboratory is cultural as much as scientific — it sets the bar that an unverifiable claim does not leave the building.',
    ],
    links: [{ label: 'uwt.xteam.pro', href: 'https://uwt.xteam.pro' }],
    research: ['unified-wholeness'],
  },
];

export const projectBySlug = new Map(projects.map((p) => [p.slug, p]));
export const isProjectSlug = (s: string) => projectBySlug.has(s);
